import type { ReactNode } from 'react';
import { ACCENTS, type Accent } from './accent';

/**
 * Friendly empty state: an illustration-style icon in a tinted badge with
 * confetti dots in the brand colours, a short title, a line of help and an
 * optional call to action. `compact` fits inside a dashboard card.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
  accent = 'teal',
  compact = false,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  accent?: Accent;
  compact?: boolean;
}) {
  const a = ACCENTS[accent];
  return (
    <div
      className={`flex items-center text-center ${
        compact ? 'gap-3 py-2 text-left' : 'flex-col gap-3 px-4 py-8'
      }`}
    >
      <span
        aria-hidden
        className={`relative flex shrink-0 items-center justify-center rounded-full ${a.soft} ${
          compact ? 'size-14' : 'size-24'
        }`}
      >
        <span className={compact ? 'size-8' : 'size-12'}>{icon}</span>
        <span className="absolute -top-0.5 right-1 size-2.5 rounded-full bg-orange" />
        <span className="absolute bottom-1 -left-1 size-2 rounded-full bg-accent" />
        {compact ? null : (
          <span className="absolute top-3 -left-2 size-1.5 rounded-full bg-brand/60" />
        )}
      </span>
      <div
        className={`flex flex-col gap-1 ${compact ? 'min-w-0 flex-1' : 'max-w-sm items-center'}`}
      >
        <p className={`font-semibold text-brand ${compact ? 'text-sm' : 'text-lg'}`}>{title}</p>
        {children ? <div className="text-sm text-muted">{children}</div> : null}
        {action ? <div className={compact ? 'mt-1' : 'mt-3'}>{action}</div> : null}
      </div>
    </div>
  );
}
